import React, { useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { motion } from 'framer-motion';
import { 
  LayoutDashboard, Users, BarChart3, Mail, Settings, 
  LogOut, Menu, X, ChevronRight, CreditCard, Package, Phone, ShieldCheck
} from 'lucide-react';
import { PERMISSIONS, hasPermission } from '../../config/permissions';
import './AdminLayout.css';

const AdminLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Define permissions for each nav item
  const navItems = [
    { path: '/admin', label: 'Dashboard', icon: LayoutDashboard, exact: true, permissions: [] }, // All can access
    { path: '/admin/users', label: 'Users', icon: Users, permissions: [PERMISSIONS.MANAGE_USERS, PERMISSIONS.VIEW_USERS] },
    { path: '/admin/sessions', label: 'Sessions', icon: BarChart3, permissions: [PERMISSIONS.VIEW_SESSIONS, PERMISSIONS.MANAGE_SESSIONS] },
    { path: '/admin/requests', label: 'Access Requests', icon: Mail, permissions: [PERMISSIONS.MANAGE_REQUESTS, PERMISSIONS.VIEW_REQUESTS] },
    { path: '/admin/consultations', label: 'Consultations', icon: Phone, permissions: [PERMISSIONS.VIEW_CONSULTATIONS, PERMISSIONS.MANAGE_CONSULTATIONS] },
    { path: '/admin/payments', label: 'Payments', icon: CreditCard, permissions: [PERMISSIONS.VIEW_PAYMENTS, PERMISSIONS.MANAGE_PAYMENTS] },
    { path: '/admin/blocked-payments', label: 'Two User Pricing', icon: ShieldCheck, permissions: [PERMISSIONS.MANAGE_SETTINGS, PERMISSIONS.VIEW_SETTINGS] },
    { path: '/admin/bundles', label: 'Bundles', icon: Package, permissions: [PERMISSIONS.MANAGE_BUNDLES, PERMISSIONS.VIEW_BUNDLES] },
    { path: '/admin/settings', label: 'Settings', icon: Settings, permissions: [PERMISSIONS.MANAGE_SETTINGS, PERMISSIONS.VIEW_SETTINGS] },
  ];

  // Get user permissions (from user.permissions or user.role)
  const userPermissions = user?.permissions;
  const userRole = user?.role || 'user';

  // Check if user has permission to access a nav item
  const checkPermission = (requiredPermissions) => {
    // If no permissions required, allow all
    if (!requiredPermissions || requiredPermissions.length === 0) return true;
    
    // If user is admin, allow all
    if (userRole === 'admin' || userRole === 'Admin' || userRole === 'ADMIN') return true;
    
    // Otherwise check specific permissions
    return hasPermission(userPermissions, requiredPermissions, userRole);
  };

  // Filter nav items based on permissions
  const filteredNavItems = navItems.filter(item => checkPermission(item.permissions));

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  return (
    <div className="admin-layout">
      {/* Sidebar */}
      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : 'closed'}`}>
        <div className="sidebar-header">
          <Link to="/admin" className="sidebar-brand">
            <span className="brand-icon">🛡️</span>
            {sidebarOpen && <span className="brand-text">Admin Panel</span>}
          </Link>
          <button 
            className="sidebar-toggle"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        <nav className="sidebar-nav">
          {filteredNavItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`nav-item ${isActive(item.path, item.exact) ? 'active' : ''}`}
            >
              <item.icon size={20} />
              {sidebarOpen && <span>{item.label}</span>}
              {sidebarOpen && isActive(item.path, item.exact) && (
                <ChevronRight size={16} className="nav-arrow" />
              )}
            </Link>
          ))}
        </nav>

        <div className="sidebar-footer">
          {sidebarOpen && (
            <div className="admin-info">
              <div className="admin-avatar">
                {user?.username?.charAt(0).toUpperCase() || 'A'}
              </div>
              <div className="admin-details">
                <span className="admin-name">{user?.username}</span>
                <span className="admin-role">
                  {userRole === 'admin' ? 'Administrator' : userRole.charAt(0).toUpperCase() + userRole.slice(1)}
                </span>
              </div>
            </div>
          )}
          <button onClick={handleLogout} className="logout-btn">
            <LogOut size={20} />
            {sidebarOpen && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        <motion.div
          key={location.pathname}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          className="admin-content"
        >
          <Outlet />
        </motion.div>
      </main>
    </div>
  );
};

export default AdminLayout;
